import { useEffect, useRef, useState } from "react";

const MODERN_LAYER = "satellite-Satellite";
const FADE_MS = 400;
const LOAD_TIMEOUT_MS = 10000;

function layerMatches(layer, id, source) {
  if (typeof id !== "undefined") {
    if (id instanceof RegExp) {
      if (layer.id.match(id)) {
        return true;
      }
    } else {
      if (layer.id === id) {
        return true;
      }
    }
  }

  if (typeof source !== "undefined") {
    if (source instanceof RegExp) {
      if (layer.source.match(source)) {
        return true;
      }
    } else {
      if (layer.source === source) {
        return true;
      }
    }
  }
}

function coverageLayerId(coverage) {
  if (!coverage) return null;
  return coverage === "Modern" ? MODERN_LAYER : `coverage-layer-${coverage}`;
}

function setOpacity(map, layerId, opacity, duration) {
  map.setPaintProperty(layerId, "raster-opacity-transition", {
    duration,
    delay: 0,
  });
  map.setPaintProperty(layerId, "raster-opacity", opacity);
}

// Calls back once visible tiles have loaded, or after a timeout so a slow or
// failing tile server can't stall switching forever
function whenTilesLoaded(map, callback) {
  const finish = () => {
    cancel();
    callback();
  };
  const timeout = setTimeout(finish, LOAD_TIMEOUT_MS);
  const cancel = () => {
    clearTimeout(timeout);
    map.off("idle", finish);
  };

  map.on("idle", finish);
  return cancel;
}

function useCoverageSelect(
  map,
  satelliteActive,
  activeCoverage,
  setActiveCoverage,
) {
  const displayedLayer = useRef(null);
  const [displayedCoverage, setDisplayedCoverage] = useState(null);

  function hideLayers({ id, source } = {}) {
    if (!map.current) return;

    map.current.getStyle().layers.forEach((layer) => {
      if (layerMatches(layer, id, source)) {
        map.current.setLayoutProperty(layer.id, "visibility", "none");
      }
    });
  }

  function showLayers({ id, source } = {}) {
    if (!map.current) return;

    map.current.getStyle().layers.forEach((layer) => {
      if (layerMatches(layer, id, source)) {
        map.current.setLayoutProperty(layer.id, "visibility", "visible");
      }
    });
  }

  useEffect(() => {
    if (!map.current) return;
    if (satelliteActive === null) return;

    if (satelliteActive) {
      showLayers({ id: /satellite-/ });

      // With a specific year (or Modern) picked, let the coverage effect load
      // and fade it in rather than loading Modern imagery first
      if (activeCoverage) {
        hideLayers({ id: MODERN_LAYER });
        displayedLayer.current = null;
      } else {
        if (map.current.getLayer(MODERN_LAYER)) {
          setOpacity(map.current, MODERN_LAYER, 1, 0);
        }
        displayedLayer.current = MODERN_LAYER;
      }
    } else {
      setActiveCoverage(null);
      hideLayers({ id: /satellite-/ });
      hideLayers({ id: /coverage-layer-/ });
      displayedLayer.current = null;
      setDisplayedCoverage(null);
    }
  }, [satelliteActive]);

  useEffect(() => {
    if (!map.current) return;
    if (satelliteActive === null) return;

    const target = coverageLayerId(activeCoverage);
    const previous = displayedLayer.current;

    if (!target) {
      hideLayers({ id: /coverage-layer-/ });
      return;
    }

    if (!map.current.getLayer(target)) return;

    if (target === previous) {
      setDisplayedCoverage(activeCoverage);
      return;
    }

    // Clear out anything left visible by an interrupted transition
    map.current.getStyle().layers.forEach((layer) => {
      if (
        (layer.id === MODERN_LAYER || layer.id.startsWith("coverage-layer-")) &&
        layer.id !== previous &&
        layer.id !== target
      ) {
        map.current.setLayoutProperty(layer.id, "visibility", "none");
      }
    });

    // Load the new layer invisibly, then fade it in over the previous one
    // before fading the previous one out, so there's never a blank map
    setOpacity(map.current, target, 0, 0);
    map.current.setLayoutProperty(target, "visibility", "visible");

    const timers = [];
    const cancelLoad = whenTilesLoaded(map.current, () => {
      setOpacity(map.current, target, 1, FADE_MS);
      displayedLayer.current = target;
      setDisplayedCoverage(activeCoverage);

      if (!previous) return;

      timers.push(
        setTimeout(() => {
          setOpacity(map.current, previous, 0, FADE_MS);

          timers.push(
            setTimeout(() => {
              map.current.setLayoutProperty(previous, "visibility", "none");
              setOpacity(map.current, previous, 1, 0);
            }, FADE_MS),
          );
        }, FADE_MS),
      );
    });

    return () => {
      cancelLoad();
      timers.forEach(clearTimeout);
    };
  }, [activeCoverage]);

  return displayedCoverage;
}

export default useCoverageSelect;
